import React, { useContext, useState, useEffect } from 'react';
import { Block } from '../../../../../../../types/shared/blocksV3';
import { DeckContext, updateBlock } from '../../../context/DeckContextV3';
import {
	Button,
	IconButton,
	Typography,
	useTheme,
	DialogTitle,
	DialogContent,
	DialogActions,
	Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockType from '../../../../../../../enums/blockType';
import LandingPopup from './LandingModalV3';
import DeckboxPopup from './DeckboxModalV3';
import StairModal from './StairModalV3';
import BumpoutModal from './BumpoutModalV3';
import { Modal } from 'src/globalComponents/modal/Modal';
import updateChildBlocks from '../../../utils/updateChildBlocks';
import ArrowPopper from '../ArrowPopper';
import getParentBlock from 'src/utils/getParentBlockV3';
import { getVacantSegmentWithoutValidation } from '../../../utils/getVacantSegment';
import { removeBlockRef } from 'src/utils/removeBlockRefs';
import { v4 as uuidv4 } from 'uuid';
import EdgeType from '../../../../../../../enums/edgeType';
import EdgeLabel from '../../../../../../../enums/edgeLabel';
import { Segment } from '../../../../../../../types/shared/blocksV3';

const BlockModal: React.FC<{ block: Block }> = ({ block }) => {
	const { state, dispatch, transformWrapperRef } = useContext(DeckContext);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [blockData, setBlockData] = useState<Block>(block);
	const theme = useTheme();

	const anchorEl = block?.ref?.current;

	useEffect(() => {
		setBlockData(block);
	}, [block]);

	const [initialBlockData] = useState<Block>(block);

	const handleCloseDialog = () => {
		if (
			JSON.stringify(removeBlockRef(block)) !==
			JSON.stringify(removeBlockRef(initialBlockData))
		) {
			dispatch({
				type: 'UPDATE_BLOCK',
				payload: initialBlockData,
			});

			const updateChildBlocksParams = {
				blocks: state.blocks,
				oldBlock: block,
				updatedBlock: initialBlockData,
				dispatch,
			};
			updateChildBlocks(updateChildBlocksParams);
		}

		dispatch({ type: 'CLOSE_BLOCK_MODAL' });
		dispatch({ type: 'CLEAR_SELECTION' });
	};

	const handleSave = () => {
		dispatch({ type: 'CLOSE_BLOCK_MODAL' });
		dispatch({ type: 'CLEAR_SELECTION' });
	};

	const handleBlockUpdate = (update: Block | ((prev: Block) => Block)) => {
		let newBlockData: Block;
		if (typeof update === 'function') {
			newBlockData = update(blockData);
		} else {
			newBlockData = update;
		}

		setBlockData(newBlockData);

		const { parentBlock } = getParentBlock(state.blocks, block.parentBlock);

		let updatedParentBlock;

		const isLandingOnStair =
			block.type === BlockType.LANDING && parentBlock?.type === BlockType.STAIR;

		if (parentBlock && !isLandingOnStair) {
			const parentEdge = parentBlock.edges.find(
				(edge) => edge.id == block.parentBlock.edgeId
			);

			updatedParentBlock = { ...parentBlock };

			let segmentAttachedTo;
			let segmentIndex;

			if (parentEdge) {
				segmentIndex = parentEdge.segments.findIndex(
					(segment) => segment.id == block.parentBlock.segmentId
				);

				segmentAttachedTo = parentEdge.segments[segmentIndex];

				const blockAttachedEdge = newBlockData.edges?.find(
					(ed) => ed.attachedToParent
				);
				const isHorizontalAttachment =
					blockAttachedEdge?.label === EdgeLabel.TOP ||
					blockAttachedEdge?.label === EdgeLabel.BOTTOM;
				const dimensionAlongEdge =
					newBlockData.type === BlockType.STAIR || isHorizontalAttachment
						? newBlockData.dimensions.width
						: newBlockData.dimensions.depth;

				updatedParentBlock = {
					...updatedParentBlock,
					edges: updatedParentBlock.edges.map((edge) => {
						if (edge.id == parentEdge.id) {
							const vacantSegment = getVacantSegmentWithoutValidation({
								blocks: state.blocks,
								selectedBlockEdge: edge,
							});

							const delta = dimensionAlongEdge - segmentAttachedTo.length;

							let updatedSegments = edge.segments.map((segment) => {
								return {
									...segment,
									length:
										segment.id == vacantSegment?.id
											? Math.max(0, segment.length - delta)
											: segment.id == segmentAttachedTo.id
												? dimensionAlongEdge
												: segment.length,
								};
							});

							if (!vacantSegment && delta < 0) {
								const newVacantSegment: Segment = {
									id: uuidv4(),
									edgeId: edge.id,
									blockId: segmentAttachedTo.blockId,
									length: -delta,
									type: EdgeType.RAILING,
								};
								updatedSegments = [...updatedSegments, newVacantSegment];
							}

							edge.segments = updatedSegments;
						}
						return edge;
					}),
					landings: (updatedParentBlock.landings || []).map((l: Block) =>
						l.id === newBlockData.id ? newBlockData : l
					),
					stairs: (updatedParentBlock.stairs || []).map((s: Block) =>
						s.id === newBlockData.id ? newBlockData : s
					),
					bumpouts: (updatedParentBlock.bumpouts || []).map((b: Block) =>
						b.id === newBlockData.id ? newBlockData : b
					),
				};
			}
		}

		let { blocks: updatedBlocks } = updateBlock(state, newBlockData);

		if (updatedParentBlock) {
			updatedBlocks = updatedBlocks.map((blk) =>
				blk.id === (updatedParentBlock as any).id ? updatedParentBlock : blk
			) as any;
		}

		dispatch({
			type: 'UPDATE_BLOCKS',
			payload: updatedBlocks,
		});

		const updateChildBlocksParams = {
			blocks: updatedBlocks,
			oldBlock: block,
			updatedBlock: newBlockData,
			dispatch,
		};

		updateChildBlocks(updateChildBlocksParams);
	};

	return (
		<ArrowPopper
			open={true}
			anchorEl={anchorEl}
			placement="right-start"
			onClickAway={handleSave}
			width="250px"
			transformWrapperRef={transformWrapperRef}
			enableCanvasPan={true}
			disablePortal={true}
		>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{ p: theme.spacing(1, 2), pb: 0 }}
			>
				<DialogTitle component="div" sx={{ p: 0 }}>
					Edit {block.label}
				</DialogTitle>
				<IconButton
					onClick={() => setShowDeleteModal(true)}
					aria-label="delete"
					size="small"
					sx={{ color: theme.palette.error.main }}
				>
					<DeleteIcon />
				</IconButton>
			</Stack>
			<DialogContent
				sx={{
					p: theme.spacing(1.25, 1.25),
					maxHeight: '60vh',
					overflowY: 'auto',
					'&::-webkit-scrollbar': {
						width: '0.375rem',
					},
					'&::-webkit-scrollbar-track': {
						background: theme.palette.grey[100],
					},
					'&::-webkit-scrollbar-thumb': {
						background: theme.palette.grey[400],
						borderRadius: '0.1875rem',
					},
					'&::-webkit-scrollbar-thumb:hover': {
						background: theme.palette.grey[500],
					},
				}}
			>
				{block.type === BlockType.LANDING && (
					<LandingPopup
						block={block}
						blockData={blockData}
						setBlockData={handleBlockUpdate}
					/>
				)}
				{block.type === BlockType.DECKBOX && (
					<DeckboxPopup
						block={block}
						blockData={blockData}
						setBlockData={handleBlockUpdate}
					/>
				)}
				{block.type === BlockType.STAIR && (
					<StairModal
						block={block}
						blockData={blockData}
						setBlockData={handleBlockUpdate}
					/>
				)}
				{block.type === BlockType.BUMPOUT && (
					<BumpoutModal
						block={block}
						blockData={blockData}
						setBlockData={handleBlockUpdate}
					/>
				)}
			</DialogContent>
			<DialogActions
				sx={{
					borderTop: '1px solid',
					borderColor: theme.palette.divider,
					p: theme.spacing(1, 1.25),
					gap: theme.spacing(0.75),
					justifyContent: 'flex-end',
				}}
			>
				<Button
					onClick={handleCloseDialog}
					variant="text"
					sx={{
						textTransform: 'none',
						color: 'black',
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					sx={{ textTransform: 'none' }}
				>
					Save
				</Button>
			</DialogActions>

			<Modal
				open={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				modalTitle={
					<Typography variant="h2" fontWeight="bold">
						Confirm Deletion
					</Typography>
				}
				modalBody={
					<Typography color="text.secondary">
						Are you sure you want to delete {block.label} and all its
						components?
					</Typography>
				}
				modalFooter={
					<>
						<Button
							variant="text"
							onClick={() => setShowDeleteModal(false)}
							sx={{ fontWeight: theme.typography.fontWeightBold }}
						>
							Cancel
						</Button>
						<Button
							onClick={() => dispatch({ type: 'DELETE_BLOCK', payload: block })}
							variant="contained"
							color="error"
							sx={{ fontWeight: theme.typography.fontWeightBold }}
						>
							Delete
						</Button>
					</>
				}
				minWidth="300px"
			/>
		</ArrowPopper>
	);
};

export default BlockModal;
